/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { UsersService } from '@treasury/domain/users';
import { WireTemplate } from '@treasury/domain/wires';
import { TmContainer } from '@treasury/presentation';
import { ellipsisIcon } from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-list-header';
import { TmPaginationControls } from '@treasury/presentation/components/vendor/tm-pagination-controls';

import { clone } from '@jack-henry/frontend-utils/functions';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-loader';
import { ContextMenuItem } from '@vaadin/context-menu';
import '@vaadin/grid';
import { columnBodyRenderer } from '@vaadin/grid/lit.js';
import '@vaadin/grid/vaadin-grid-column.js';
import { css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { filterTableResults } from '../../../../utilities/table-result-filter';
import '../../../components/jhd-status';
import '../../../components/templates/treasury-status-column.template';
import type { WireAction } from '../../types/wire-workflow-parameters.type';

export const tagName = 'wire-templates-table';
@customElement(tagName)
export class WireTemplatesTable extends TmContainer {
    @property({ type: Array })
    public wireTemplates: WireTemplate[] = [];

    @property({ type: String, reflect: true })
    public action?: WireAction;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private usersService: UsersService;

    @state()
    private fieldsToFilter = ['name', 'debitAccountName', 'beneficiaryName', 'type'];

    @state()
    private filteredWireTemplates: WireTemplate[] = [];

    @state()
    private filteredWireTemplatesCount: number = 0;

    @state()
    private searchTerm: string = '';

    @state()
    private currentPage: number = 1;

    @query('tm-pagination-controls')
    private paginationControls!: TmPaginationControls;

    protected render() {
        return html`<div class="flex flex-col h-full">
            <div
                class=${classMap({
                    'flex-none': true,
                    'bg-primary': !this.isDesktop,
                })}
            >
                <tm-list-header
                    .filterButton=${true}
                    @filterButtonClick=${() =>
                        this.dispatchEvent(new CustomEvent('filter-wire-templates'))}
                    @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
                ></tm-list-header>
            </div>

            <div class="flex-grow">${this.renderTable()}</div>
            <div class="flex-none">${this.renderPaginationControls()}</div>
        </div>`;
    }

    protected renderPaginationControls() {
        if (!this.isDesktop) {
            return nothing;
        }
        return html`<tm-pagination-controls
            .totalItemCount=${this.filteredWireTemplatesCount}
            .currentPage=${this.currentPage}
            .itemLabel=${'wire templates'}
            @page-changed=${(e: CustomEvent) => {
                this.currentPage = e.detail.currentPage;
                this.filterResults();
            }}
        ></tm-pagination-controls>`;
    }

    protected updated(_changedProperties: Map<PropertyKey, unknown>): void {
        if (_changedProperties.has('wireTemplates')) {
            this.filteredWireTemplates = this.wireTemplates;
            this.currentPage = 1;
            this.searchTerm = '';
            this.filterResults({ detail: { value: this.searchTerm } } as CustomEvent);
            if (this.action === 'initiate') {
                this.filteredWireTemplates = this.wireTemplates.filter(
                    wireTemplate => wireTemplate.status === 'Ready'
                );
            }
        }
    }

    private onTemplateClick(wireTemplate: WireTemplate) {
        if (!wireTemplate) {
            return;
        }
        this.dispatchEvent(
            new CustomEvent('select-template', {
                detail: { value: wireTemplate },
            })
        );
    }

    private filterResults(e?: CustomEvent) {
        let filteredWireTemplates = clone(this.wireTemplates);
        if (e) {
            if (typeof e === 'string') {
                this.searchTerm = '';
            } else {
                this.searchTerm = e.detail.value;
            }
        }
        filteredWireTemplates = filterTableResults(
            { detail: { value: this.searchTerm } } as CustomEvent,
            this.wireTemplates,
            this.fieldsToFilter
        ) as WireTemplate[];
        this.filteredWireTemplates = filteredWireTemplates;
        this.filteredWireTemplatesCount = filteredWireTemplates.length;
        const offset = this.paginationControls?.calculateOffset();
        const limit = this.paginationControls?.pageSize;
        this.filteredWireTemplates = filteredWireTemplates.slice(offset, offset + limit);
    }

    private renderTable() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        if (!this.filteredWireTemplates.length) {
            return html`<div class="w-full px-4 py-8 text-center text-sm text-secondary">
                No wire templates found
            </div>`;
        }
        return html`<vaadin-grid
            class=${classMap({
                'h-full': true,
                'shadow-md': !this.isDesktop,
            })}
            .items=${this.filteredWireTemplates}
            @active-item-changed=${({ detail }: CustomEvent) => {
                this.onTemplateClick(detail.value);
            }}
        >
            ${this.deviceService.isDesktop
                ? this.renderDesktopColumns()
                : this.renderMobileColumns()}
        </vaadin-grid>`;
    }

    private renderDesktopColumns() {
        return html`<vaadin-grid-sort-column
                header="Template Name"
                path="name"
                frozen
                auto-width
                flex-grow="0"
                ${columnBodyRenderer(
                    (wireTemplate: WireTemplate) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${wireTemplate.name}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Wire Type"
                path="isInternational"
                ${columnBodyRenderer(
                    (wireTemplate: WireTemplate) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm"
                                >${wireTemplate.isInternational
                                    ? 'International'
                                    : 'Domestic'}</span
                            >
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Wire Company"
                path="wireCompany.name"
                ${columnBodyRenderer(
                    (wireTemplate: WireTemplate) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${wireTemplate.wireCompany?.name}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Debit Account"
                path="debitAccount.name"
                ${columnBodyRenderer(
                    (wireTemplate: WireTemplate) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${wireTemplate.debitAccount?.name}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Creditor Name"
                path="beneficiary.name"
                ${columnBodyRenderer(
                    (wireTemplate: WireTemplate) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${wireTemplate.beneficiary?.name}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Status"
                path="status"
                width="150px"
                ${columnBodyRenderer((payment: WireTemplate) => {
                    return html`<treasury-status-column
                        .item=${payment}
                        .completedApproversFunction=${async () =>
                            this.usersService.fetchCompletedApprovers({
                                approvalEntity: 'wireTemplate',
                                productId: payment.id as number | undefined,
                                createdBy: payment.createdBy as unknown as number,
                            }) as unknown as string[]}
                        .eligibleApproversFunction=${async () =>
                            this.usersService.fetchAvailableApprovers({
                                approvalEntity: 'wireTemplate',
                                productId: payment.id as number | undefined,
                                createdBy: payment.createdBy as unknown as number,
                            }) as unknown as string[]}
                    ></treasury-status-column>`;
                })}
            ></vaadin-grid-sort-column>
            ${this.action === 'view'
                ? html`<vaadin-grid-column
                      frozen-to-end
                      flex-grow="0"
                      width="3rem"
                      text-align="end"
                      ${columnBodyRenderer((wireTemplate: WireTemplate) => {
                          const actions: Array<ContextMenuItem> =
                              this.getTemplateActions(wireTemplate);
                          return html`<tm-context-menu
                              .openOn=${'click'}
                              .items=${actions}
                              @item-selected=${(e: CustomEvent) => {
                                  const action = e.detail.value.text;
                                  this.handleActionSelected(action, wireTemplate);
                              }}
                              ><tm-button importance="tertiary"
                                  ><tm-icon
                                      .icon=${ellipsisIcon}
                                      color="var(--jh-color-gray-900)"
                                  ></tm-icon></tm-button
                          ></tm-context-menu>`;
                      }, [])}
                  ></vaadin-grid-column>`
                : nothing}`;
    }

    private renderMobileColumns() {
        return html`<vaadin-grid-column
            flex-grow="1"
            auto-width
            ${columnBodyRenderer(
                (wireTemplate: WireTemplate) =>
                    html`<div class="py-2 pr-4">
                        <div class="flex justify-between items-center">
                            <div class="template-name text-sm">
                                <div class="font-bold">${wireTemplate.name}</div>
                                <div
                                    class="template-debit-beneficiary text-xs text-secondary whitespace-normal"
                                >
                                    from ${wireTemplate.debitAccount?.accountDisplayLabel} to
                                    ${wireTemplate.beneficiary?.name}
                                </div>
                            </div>
                            <div class="template-wire-type-column text-right font-bold text-sm">
                                ${wireTemplate.isInternational ? 'International' : 'Domestic'}
                            </div>
                        </div>
                    </div>`,
                []
            )}
        ></vaadin-grid-column>`;
    }

    private getTemplateActions(wireTemplate: WireTemplate): Array<ContextMenuItem> {
        const actions: Array<ContextMenuItem> = [];
        const permissions = wireTemplate.permissions.map(p => p.permission);
        if (wireTemplate.status === 'Ready') {
            actions.push({ text: 'Initiate' });
        }
        actions.push({ text: 'View' });
        if (permissions.includes('Edit')) {
            actions.push({
                text: 'Edit',
            });
        }
        if (permissions.includes('Delete')) {
            actions.push({
                text: 'Delete',
            });
        }
        return actions;
    }

    private handleActionSelected(action: string, template: WireTemplate) {
        switch (action) {
            case 'Initiate':
                this.navService.navigate(`/wires/template/initiate/${template.id}/usd`);
                break;
            case 'View':
                this.navService.navigate(`/wires/template/${template.id}`);
                break;
            case 'Edit':
                this.navService.navigate(`/wires/template/edit/${template.id}/usd`);
                break;
            case 'Delete':
                this.dispatchEvent(
                    new CustomEvent('delete-template', {
                        detail: template.id,
                    })
                );
                break;
            default:
                break;
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                }
                vaadin-grid::part(header-cell) {
                    min-height: auto;
                    padding-top: 3px;
                    background-color: var(--border-color);
                    font-size: 0.75rem;
                    line-height: calc(1 / 0.75);
                }
                vaadin-grid-sorter::part(indicators) {
                    opacity: 0.5;
                }
                vaadin-grid-sorter[direction]::part(indicators) {
                    opacity: 100;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireTemplatesTable;
    }
}
